import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    HostListener,
    InjectionToken,
    inject
} from "@angular/core";
import { ISnackbarButton, ISnackbarOptions } from "./lg-snackbar.types";
import { OverlayRef } from "@angular/cdk/overlay";
import { LgTranslateService } from "@logex/framework/lg-localization";
import { animate, AnimationEvent, state, style, transition, trigger } from "@angular/animations";
import { NgForOf, NgIf } from "@angular/common";
import { LgIconComponent } from "../controls";
import { LgButtonComponent } from "../controls/lg-button.component";

export const LG_SNACKBAR_OPTIONS = new InjectionToken<ISnackbarOptions>("LgSnackbarOptions");

@Component({
    standalone: true,
    selector: "lg-snackbar",
    templateUrl: "./lg-snackbar.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { "[style.flex-grow]": "1" },
    imports: [NgIf, NgForOf, LgIconComponent, LgButtonComponent],
    animations: [
        trigger("basicAnimation", [
            state("closed", style({ opacity: 0 })),
            transition("* => opened", [
                style({ transform: "translateX(-100%)" }),
                animate("250ms ease-out")
            ]),
            transition("* => closed", animate(250))
        ]),
        trigger("moveUpAnimation", [
            transition("* => moved", [
                style({ transform: "translateY({{ moveUpOffset }}px)" }),
                animate("250ms ease-out")
            ])
        ])
    ]
})
export class LgSnackbarComponent implements AfterViewInit {
    options = inject(LG_SNACKBAR_OPTIONS);
    private _overlayRef = inject(OverlayRef);
    private _translate = inject(LgTranslateService);
    private _changeDetectorRef = inject(ChangeDetectorRef);

    @HostBinding("@basicAnimation")
    get _animationBasicState(): string {
        return this._visibilityState;
    }

    @HostListener("@basicAnimation.done", ["$event"])
    _animationStateDone(event: AnimationEvent): void {
        if (event.fromState === "opened" && event.toState === "closed") {
            this._overlayRef.detach();
        }
    }

    // ---------------------------------------------------------------------------------------------
    @HostBinding("@moveUpAnimation")
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get _animationMoveUpState() {
        return {
            value: this._moveUpState,
            params: {
                moveUpOffset: this._moveUpOffset
            }
        };
    }

    @HostListener("@moveUpAnimation.done", ["$event"])
    _animationMoveUpDone(event: AnimationEvent): void {
        if (event.toState === "moved") {
            this._moveUpState = "initial";
        }
    }

    // ---------------------------------------------------------------------------------------------
    _visibilityState: "opened" | "closed" = "closed";

    _moveUpState: "initial" | "moved" = "initial";
    _moveUpOffset: number | undefined;

    _title: string | undefined;
    _text: string | undefined;

    constructor() {
        this._title = this.options.titleLc
            ? this._translate.translate(this.options.titleLc)
            : this.options.title;

        this._text = this.options.textLc
            ? this._translate.translate(this.options.textLc)
            : this.options.text;
    }

    ngAfterViewInit(): void {
        requestAnimationFrame(() => {
            this._visibilityState = "opened";
            this._changeDetectorRef.markForCheck();
        });
    }

    hide(): void {
        this._visibilityState = "closed";
        this._changeDetectorRef.markForCheck();
    }

    _onButtonClick(button: ISnackbarButton): void {
        button.callback().then(close => {
            if (close) this.hide();
        });
    }

    moveUp(offset: number): void {
        this._moveUpOffset = offset;
        this._moveUpState = "moved";
        this._changeDetectorRef.markForCheck();
    }
}
